import React from "react";

import * as Select from "@radix-ui/react-select";
import { CheckIcon, ChevronDown, ChevronUp } from "lucide-react";
import { getCountries, getCountryCallingCode } from "react-phone-number-input";
import getUnicodeFlagIcon from "country-flag-icons/unicode";

import { cn } from "@/lib/utils";

export const COUNTRY_CODE_US = "US";

const SelectItem = React.forwardRef<
  React.ElementRef<typeof Select.Item>,
  React.ComponentPropsWithoutRef<typeof Select.Item> & {
    inset?: boolean;
  }
>(({ children, className, ...props }, forwardedRef) => {
  return (
    <Select.Item
      className={cn("relative flex h-9 items-center pl-6 text-sm", className)}
      {...props}
      ref={forwardedRef}
    >
      <Select.ItemIndicator className="absolute left-0 inline-flex w-[25px] items-center justify-center">
        <CheckIcon />
      </Select.ItemIndicator>
      <Select.ItemText>{children}</Select.ItemText>
    </Select.Item>
  );
});
SelectItem.displayName = "SelectItem";

type CountrySelectProps = {
  countryCode: string;
  showCountryCallingCode?: boolean;
  onValueChange: (value: string) => void;
} & React.HTMLAttributes<HTMLDivElement>;

const CountrySelect = ({
  countryCode,
  showCountryCallingCode = false,
  onValueChange,
  className,
}: CountrySelectProps) => {
  return (
    <Select.Root value={countryCode} onValueChange={onValueChange}>
      <Select.Trigger
        className={cn(
          "flex h-12 w-full items-center justify-center rounded-lg border border-input bg-muted px-3 py-2 text-foreground transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50",
          className
        )}
      >
        <Select.Value placeholder="Select country" />
        <Select.Icon className="text-foreground">
          <ChevronDown className="-mr-1 ml-2 h-4 w-4" />
        </Select.Icon>
      </Select.Trigger>
      <Select.Portal>
        <Select.Content className="z-50 min-w-[8rem] origin-[var(--radix-select-content-transform-origin)] overflow-hidden rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2">
          <Select.ScrollUpButton className="flex h-[25px] cursor-default items-center justify-center bg-popover text-popover-foreground">
            <ChevronUp className="h-4" />
          </Select.ScrollUpButton>
          <Select.Viewport className="p-3">
            {getCountries().map((country) => (
              <SelectItem
                key={country}
                value={country}
                className="cursor-pointer transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
              >
                <span className="text-sm font-normal text-foreground">
                  {getUnicodeFlagIcon(country)} {country}{" "}
                  {showCountryCallingCode &&
                    `(+${getCountryCallingCode(country)})`}
                </span>
              </SelectItem>
            ))}
          </Select.Viewport>
          <Select.ScrollDownButton className="flex h-9 cursor-default items-center justify-center bg-popover text-popover-foreground">
            <ChevronDown className="h-4" />
          </Select.ScrollDownButton>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
};
CountrySelect.displayName = "CountrySelect";

export default CountrySelect;
